// pdfReportGenerator.js - PDF Report Generation Utility
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toPng, toSvg } from 'html-to-image';
import { debugLog } from './debugLog';

// Page dimensions for landscape orientation (A4)
const PAGE_WIDTH = 297; // mm
const PAGE_HEIGHT = 210; // mm
const MARGIN = 15; // mm
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
const CONTENT_HEIGHT = PAGE_HEIGHT - (MARGIN * 2);

/**
 * Generate a comprehensive PDF report for the current project
 * @param {Object} projectData - All project data needed for report
 */
export const generateProjectReport = async (projectData) => {
  const {
    projectName,
    projectDescription,
    createdAt,
    updatedAt,
    nodes,
    edges,
    calculationResults,
    frequencyMode,
    frequencySingle,
    frequencyStart,
    frequencyStop,
    frequencyStep
  } = projectData;

  // Create PDF in landscape orientation
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  let currentY = MARGIN;
  let currentPage = 1;

  // Load the GRF logo for watermark, convert to grayscale, and rotate -45 degrees
  let logoDataUrl = null;
  try {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      logoImg.onload = resolve;
      logoImg.onerror = reject;
      logoImg.src = '/assets/GRF.webp';
    });

    // First canvas: convert to grayscale
    const grayCanvas = document.createElement('canvas');
    grayCanvas.width = logoImg.width;
    grayCanvas.height = logoImg.height;
    const grayCtx = grayCanvas.getContext('2d');
    grayCtx.drawImage(logoImg, 0, 0);

    // Convert to grayscale
    const imageData = grayCtx.getImageData(0, 0, grayCanvas.width, grayCanvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114);
      data[i] = gray;     // R
      data[i + 1] = gray; // G
      data[i + 2] = gray; // B
    }
    grayCtx.putImageData(imageData, 0, 0);

    // Second canvas: rotate -45 degrees (other direction)
    // Calculate new canvas size to fit rotated image
    const angle = -45 * Math.PI / 180;
    const sin = Math.abs(Math.sin(angle));
    const cos = Math.abs(Math.cos(angle));
    const newWidth = logoImg.width * cos + logoImg.height * sin;
    const newHeight = logoImg.width * sin + logoImg.height * cos;

    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = newWidth;
    rotatedCanvas.height = newHeight;
    const rotatedCtx = rotatedCanvas.getContext('2d');

    // Move to center, rotate, draw, then restore
    rotatedCtx.translate(newWidth / 2, newHeight / 2);
    rotatedCtx.rotate(angle);
    rotatedCtx.drawImage(grayCanvas, -logoImg.width / 2, -logoImg.height / 2);

    logoDataUrl = {
      data: rotatedCanvas.toDataURL('image/png'),
      aspectRatio: newWidth / newHeight
    };
  } catch (err) {
    console.warn('Could not load GRF logo for watermark:', err);
  }

  // Helper function to add tiled watermark to current page
  const addWatermark = () => {
    if (!logoDataUrl) return;

    // Tile size - smaller for tiling effect
    const tileHeight = 60; // mm
    const tileWidth = tileHeight * logoDataUrl.aspectRatio;

    // Spacing between tiles
    const spacingX = tileWidth + 20; // mm between tile centers horizontally
    const spacingY = tileHeight + 20; // mm between tile centers vertically

    // ===== MANUAL ADJUSTMENT =====
    // Adjust these values to "pan" the watermark pattern
    // Positive X = shift pattern right, Negative X = shift pattern left
    // Positive Y = shift pattern down, Negative Y = shift pattern up
    const offsetX = 25;  // mm - adjust to shift horizontally
    const offsetY = 25; // mm - adjust to shift vertically (positive = down)
    // =============================

    // Set 10% opacity
    const gState = pdf.GState({ opacity: 0.10 });
    pdf.setGState(gState);

    // Tile across the entire page
    // Start slightly off-page to ensure full coverage, then apply offset
    const startX = -tileWidth / 2 + offsetX;
    const startY = -tileHeight / 2 + offsetY;

    for (let y = startY; y < PAGE_HEIGHT + tileHeight; y += spacingY) {
      for (let x = startX; x < PAGE_WIDTH + tileWidth; x += spacingX) {
        pdf.addImage(
          logoDataUrl.data,
          'PNG',
          x,
          y,
          tileWidth,
          tileHeight
        );
      }
    }

    // Restore full opacity
    const fullOpacity = pdf.GState({ opacity: 1.0 });
    pdf.setGState(fullOpacity);
  };

  // Helper function to add a new page if needed
  const checkPageBreak = (neededHeight) => {
    if (currentY + neededHeight > PAGE_HEIGHT - MARGIN) {
      pdf.addPage();
      currentPage++;
      currentY = MARGIN;
      addWatermark();
      addPageNumber();
      return true;
    }
    return false;
  };

  // Helper function to add page number (and optional footer text)
  const addPageNumber = (footerText = null) => {
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);

    // Always center the page number
    pdf.text(`Page ${currentPage}`, PAGE_WIDTH / 2, PAGE_HEIGHT - 5, { align: 'center' });

    // If footer text provided, put it right-aligned
    if (footerText) {
      pdf.text(footerText, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 5, { align: 'right' });
    }
    pdf.setTextColor(0, 0, 0);
  };

  // ============================================================================
  // TITLE PAGE
  // ============================================================================

  // Add watermark to first page
  addWatermark();

  // Title
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('RF Signal Chain Analysis Report', PAGE_WIDTH / 2, currentY, { align: 'center' });
  currentY += 15;

  // Project name
  pdf.setFontSize(18);
  pdf.text(projectName || 'Untitled Project', PAGE_WIDTH / 2, currentY, { align: 'center' });
  currentY += 8;

  // Horizontal line
  pdf.setDrawColor(200, 200, 200);
  pdf.line(MARGIN + 20, currentY, PAGE_WIDTH - MARGIN - 20, currentY);
  currentY += 5;

  // ============================================================================
  // VISUAL CANVAS SNAPSHOT - On Title Page
  // ============================================================================

  // Reserve space for canvas image - maximize available space
  // Page is 210mm tall, currentY is ~43mm, footer needs ~10mm, so ~155mm available
  const canvasPlaceholderTitle = {
    x: MARGIN,
    y: currentY,
    width: CONTENT_WIDTH,
    height: 150, // Will be adjusted based on aspect ratio
    page: currentPage
  };

  // Build footer text with timestamps
  const reportDate = new Date().toLocaleString();
  let footerText = `Generated: ${reportDate}`;
  if (updatedAt) {
    footerText += ` | Modified: ${new Date(updatedAt).toLocaleString()}`;
  }

  // Page number and timestamps in footer
  addPageNumber(footerText);

  // ============================================================================
  // SECTION 1: PROJECT METADATA & DOCUMENTATION
  // ============================================================================

  pdf.addPage();
  currentPage++;
  currentY = MARGIN;
  addWatermark();

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('1. Project Information', MARGIN, currentY);
  currentY += 10;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');

  const metadata = [
    ['Project Name:', projectName || 'Untitled'],
    ['Description:', projectDescription || 'No description provided'],
    ['Total Components:', nodes?.length || 0],
    ['Total Connections:', edges?.length || 0],
    ['Frequency Mode:', frequencyMode === 'single' ? 'Single Frequency' : 'Frequency Sweep'],
  ];

  metadata.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, MARGIN, currentY);
    pdf.setFont('helvetica', 'normal');
    const valueLines = pdf.splitTextToSize(String(value), CONTENT_WIDTH - 50);
    pdf.text(valueLines, MARGIN + 50, currentY);
    currentY += Math.max(6, valueLines.length * 5);
  });

  addPageNumber();

  // ============================================================================
  // SECTION 2: FREQUENCY ANALYSIS
  // ============================================================================

  checkPageBreak(40);
  currentY += 10;

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('2. Frequency Analysis', MARGIN, currentY);
  currentY += 10;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');

  if (frequencyMode === 'single') {
    pdf.text(`Operating Frequency: ${frequencySingle || 2400} MHz`, MARGIN, currentY);
    currentY += 6;
    pdf.text('Analysis Mode: Single frequency point analysis', MARGIN, currentY);
    currentY += 6;
  } else {
    pdf.text(`Frequency Sweep Configuration:`, MARGIN, currentY);
    currentY += 6;
    pdf.text(`  Start: ${frequencyStart || 2000} MHz`, MARGIN + 5, currentY);
    currentY += 6;
    pdf.text(`  Stop: ${frequencyStop || 3000} MHz`, MARGIN + 5, currentY);
    currentY += 6;
    pdf.text(`  Step: ${frequencyStep || 10} MHz`, MARGIN + 5, currentY);
    currentY += 6;
    pdf.text(`  Bandwidth: ${(frequencyStop - frequencyStart) || 1000} MHz`, MARGIN + 5, currentY);
    currentY += 6;
  }

  addPageNumber();

  // ============================================================================
  // SECTION 3: SIGNAL CHAIN PERFORMANCE SUMMARY
  // ============================================================================

  // Start Section 3 on a new page
  pdf.addPage();
  currentPage++;
  currentY = MARGIN;
  addWatermark();

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('3. Signal Chain Performance Summary', MARGIN, currentY);
  currentY += 10;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');

  // Calculate overall system performance
  const performanceData = calculateSystemPerformance(nodes, calculationResults);

  // Get system-level metrics from path_0 calculation results
  const systemMetrics = calculationResults?.path_0 || {};

  if (performanceData.hasData) {
    pdf.text(`Total System Gain: ${performanceData.totalGain} dB`, MARGIN, currentY);
    currentY += 6;

    if (performanceData.outputPower !== null) {
      pdf.text(`Output Power: ${performanceData.outputPower} dBm`, MARGIN, currentY);
      currentY += 6;
    }

    // System Noise Figure
    if (systemMetrics.noiseFigure) {
      pdf.text(`System Noise Figure: ${systemMetrics.noiseFigure} dB`, MARGIN, currentY);
      currentY += 6;
    }

    // System OIP3
    if (systemMetrics.oip3 && systemMetrics.oip3 !== 'N/A') {
      pdf.text(`System OIP3: ${systemMetrics.oip3} dBm`, MARGIN, currentY);
      currentY += 6;
    }

    // System OP1dB
    if (systemMetrics.p1db && systemMetrics.p1db !== 'N/A') {
      pdf.text(`System OP1dB: ${systemMetrics.p1db} dBm`, MARGIN, currentY);
      currentY += 6;
    }

    if (performanceData.compressionWarnings > 0) {
      pdf.setTextColor(255, 140, 0); // Orange
      pdf.text(`⚠ Compression Warnings: ${performanceData.compressionWarnings} component(s)`, MARGIN, currentY);
      currentY += 6;
      pdf.setTextColor(0, 0, 0);
    }

    if (performanceData.compressionErrors > 0) {
      pdf.setTextColor(255, 0, 0); // Red
      pdf.text(`✖ Compression Errors: ${performanceData.compressionErrors} component(s)`, MARGIN, currentY);
      currentY += 6;
      pdf.setTextColor(0, 0, 0);
    }

    currentY += 5;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Component-Level Performance:', MARGIN, currentY);
    currentY += 8;
    pdf.setFont('helvetica', 'normal');

    // Table header
    const colX = [MARGIN, MARGIN + 70, MARGIN + 130, MARGIN + 170, MARGIN + 210];
    pdf.setFillColor(230, 230, 230);
    pdf.rect(MARGIN, currentY - 5, CONTENT_WIDTH, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.text('Component', colX[0] + 2, currentY);
    pdf.text('Gain (dB)', colX[1] + 2, currentY);
    pdf.text('Pout (dBm)', colX[2] + 2, currentY);
    pdf.text('Cascaded Gain', colX[3] + 2, currentY);
    pdf.text('Status', colX[4] + 2, currentY);
    currentY += 8;

    pdf.setFont('helvetica', 'normal');

    // Table rows
    performanceData.componentDetails.forEach((comp, idx) => {
      checkPageBreak(8);

      // Fill all rows with white to prevent watermark showing through
      pdf.setFillColor(idx % 2 === 0 ? 255 : 245, idx % 2 === 0 ? 255 : 245, idx % 2 === 0 ? 255 : 245);
      pdf.rect(MARGIN, currentY - 5, CONTENT_WIDTH, 8, 'F');

      pdf.text(comp.name, colX[0] + 2, currentY);
      pdf.text(comp.gain, colX[1] + 2, currentY);
      pdf.text(comp.outputPower, colX[2] + 2, currentY);
      pdf.text(comp.cascadedGain, colX[3] + 2, currentY);

      // Status with color
      if (comp.status === 'Compressed') {
        pdf.setTextColor(255, 0, 0);
      } else if (comp.status === 'Warning') {
        pdf.setTextColor(255, 140, 0);
      } else {
        pdf.setTextColor(0, 150, 0);
      }
      pdf.text(comp.status, colX[4] + 2, currentY);
      pdf.setTextColor(0, 0, 0);

      currentY += 8;
    });
  } else {
    pdf.text('No performance data available. Add components to the signal chain.', MARGIN, currentY);
    currentY += 6;
  }

  addPageNumber();

  // ============================================================================
  // SECTION 4: COMPONENT INVENTORY TABLE
  // ============================================================================

  pdf.addPage();
  currentPage++;
  currentY = MARGIN;
  addWatermark();

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('4. Component Inventory', MARGIN, currentY);
  currentY += 10;

  pdf.setFontSize(10);

  // Filter out source and measurement nodes for inventory
  const excludedTypes = ['inputSource', 'noiseSource', 'measurement'];
  const inventoryNodes = nodes?.filter(node => !excludedTypes.includes(node.type)) || [];

  if (inventoryNodes.length > 0) {
    // Count components by category (excluding sources and measurement)
    const categoryCounts = countComponentsByCategory(inventoryNodes);

    pdf.setFont('helvetica', 'normal');
    pdf.text('Component Summary:', MARGIN, currentY);
    currentY += 6;

    Object.entries(categoryCounts).forEach(([category, count]) => {
      pdf.text(`  ${category}: ${count}`, MARGIN + 5, currentY);
      currentY += 5;
    });

    currentY += 10;

    // Detailed component table
    pdf.setFont('helvetica', 'bold');
    pdf.text('Detailed Component List:', MARGIN, currentY);
    currentY += 8;

    // Table header - adjusted columns for Package and Datasheet
    const invColX = [MARGIN, MARGIN + 45, MARGIN + 95, MARGIN + 135, MARGIN + 175, MARGIN + 230];
    pdf.setFillColor(230, 230, 230);
    pdf.rect(MARGIN, currentY - 5, CONTENT_WIDTH, 8, 'F');
    pdf.text('Alias/Name', invColX[0] + 2, currentY);
    pdf.text('Type', invColX[1] + 2, currentY);
    pdf.text('Part Number', invColX[2] + 2, currentY);
    pdf.text('Package', invColX[3] + 2, currentY);
    pdf.text('Key Parameters', invColX[4] + 2, currentY);
    pdf.text('Datasheet', invColX[5] + 2, currentY);
    currentY += 8;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);

    // Table rows
    inventoryNodes.forEach((node, idx) => {
      checkPageBreak(10);

      // Fill all rows with white to prevent watermark showing through
      pdf.setFillColor(idx % 2 === 0 ? 255 : 245, idx % 2 === 0 ? 255 : 245, idx % 2 === 0 ? 255 : 245);
      pdf.rect(MARGIN, currentY - 5, CONTENT_WIDTH, 8, 'F');

      const alias = node.data?.alias || node.data?.name || node.type || 'Unknown';
      const type = node.type || 'Unknown';
      const partNumber = node.data?.partNumber || (node.data?.isCatalog ? 'Catalog' : 'Generic');
      const packageInfo = node.data?.parameters?.package?.value || '-';
      const keyParams = getKeyParameters(node);

      pdf.text(alias.substring(0, 18), invColX[0] + 2, currentY);
      pdf.text(type.substring(0, 15), invColX[1] + 2, currentY);
      pdf.text(partNumber.substring(0, 15), invColX[2] + 2, currentY);
      pdf.text(packageInfo.substring(0, 12), invColX[3] + 2, currentY);
      pdf.text(keyParams.substring(0, 25), invColX[4] + 2, currentY);

      // Add datasheet link for catalog components with GRF part numbers
      if (partNumber && partNumber.startsWith('GRF')) {
        const sku = partNumber.replace(/^GRF/, '');
        const datasheetUrl = `https://www.guerrilla-rf.com/products/DataSheet?sku=${sku}&file_name=${partNumber}DS.pdf`;
        pdf.setTextColor(0, 0, 255);
        pdf.textWithLink('View DS', invColX[5] + 2, currentY, { url: datasheetUrl });
        pdf.setTextColor(0, 0, 0);
      } else {
        pdf.text('-', invColX[5] + 2, currentY);
      }

      currentY += 8;
    });
  } else {
    pdf.setFont('helvetica', 'normal');
    pdf.text('No components in this project.', MARGIN, currentY);
  }

  addPageNumber();

  // ============================================================================
  // SECTION 5: BILL OF MATERIALS (BOM)
  // ============================================================================

  // Only generate BOM if there are catalog components
  const catalogComponents = nodes?.filter(node =>
    node.data?.partNumber && node.data.partNumber.startsWith('GRF')
  ) || [];

  if (catalogComponents.length > 0) {
    pdf.addPage();
    currentPage++;
    currentY = MARGIN;
    addWatermark();

    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('5. Bill of Materials (BOM)', MARGIN, currentY);
    currentY += 10;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Guerrilla RF Components:', MARGIN, currentY);
    currentY += 8;

    // Aggregate parts by part number
    const bomAggregated = {};
    catalogComponents.forEach(node => {
      const partNumber = node.data.partNumber;
      const packageInfo = node.data?.parameters?.package?.value || '-';
      const technology = node.data?.parameters?.technology?.value || '-';
      const freqMin = node.data?.parameters?.frequencyMin?.value;
      const freqMax = node.data?.parameters?.frequencyMax?.value;
      const freqRange = (freqMin && freqMax) ? `${freqMin}-${freqMax} MHz` : '-';

      // Get component type (LNA, PA, etc.)
      let componentType = '-';
      const name = node.data?.name || '';
      if (name.includes('LNA') || node.type?.includes('lna')) {
        componentType = 'LNA';
      } else if (name.includes('PA') || name.includes('Power Amp') || node.type?.includes('pa')) {
        componentType = 'Power Amplifier';
      } else if (name.includes('Gain Block') || node.type?.includes('gainBlock')) {
        componentType = 'Gain Block';
      } else if (name.includes('Driver')) {
        componentType = 'Driver';
      } else if (node.type?.includes('amplifier')) {
        componentType = 'Amplifier';
      }

      if (!bomAggregated[partNumber]) {
        bomAggregated[partNumber] = {
          partNumber,
          package: packageInfo,
          quantity: 0,
          componentType,
          technology,
          freqRange
        };
      }
      bomAggregated[partNumber].quantity++;
    });

    // Table header - adjusted columns for new fields
    const bomColX = [MARGIN, MARGIN + 30, MARGIN + 65, MARGIN + 115, MARGIN + 165, MARGIN + 215, MARGIN + 245];
    pdf.setFillColor(230, 230, 230);
    pdf.rect(MARGIN, currentY - 5, CONTENT_WIDTH, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.text('Part Number', bomColX[0] + 2, currentY);
    pdf.text('Type', bomColX[1] + 2, currentY);
    pdf.text('Package', bomColX[2] + 2, currentY);
    pdf.text('Technology', bomColX[3] + 2, currentY);
    pdf.text('Freq Range', bomColX[4] + 2, currentY);
    pdf.text('Qty', bomColX[5] + 2, currentY);
    pdf.text('Datasheet', bomColX[6] + 2, currentY);
    currentY += 8;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);

    // Table rows
    Object.values(bomAggregated).forEach((item, idx) => {
      checkPageBreak(10);

      // Fill all rows with white to prevent watermark showing through
      pdf.setFillColor(idx % 2 === 0 ? 255 : 245, idx % 2 === 0 ? 255 : 245, idx % 2 === 0 ? 255 : 245);
      pdf.rect(MARGIN, currentY - 5, CONTENT_WIDTH, 8, 'F');

      pdf.text(item.partNumber, bomColX[0] + 2, currentY);
      pdf.text(item.componentType.substring(0, 12), bomColX[1] + 2, currentY);
      pdf.text(item.package.substring(0, 18), bomColX[2] + 2, currentY);
      pdf.text(item.technology.substring(0, 18), bomColX[3] + 2, currentY);
      pdf.text(item.freqRange.substring(0, 18), bomColX[4] + 2, currentY);
      pdf.text(String(item.quantity), bomColX[5] + 2, currentY);

      // Datasheet link
      const sku = item.partNumber.replace(/^GRF/, '');
      const datasheetUrl = `https://www.guerrilla-rf.com/products/DataSheet?sku=${sku}&file_name=${item.partNumber}DS.pdf`;
      pdf.setTextColor(0, 0, 255);
      pdf.textWithLink('View DS', bomColX[6] + 2, currentY, { url: datasheetUrl });
      pdf.setTextColor(0, 0, 0);

      currentY += 8;
    });

    currentY += 10;
    pdf.setFontSize(10);
    pdf.text(`Total Unique Parts: ${Object.keys(bomAggregated).length}`, MARGIN, currentY);
    currentY += 6;
    pdf.text(`Total Component Count: ${catalogComponents.length}`, MARGIN, currentY);

    addPageNumber();
  }

  // ============================================================================
  // SECTION 6: CONTACT INFORMATION
  // ============================================================================

  pdf.addPage();
  currentPage++;
  currentY = MARGIN;
  addWatermark();

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Contact Guerrilla RF', PAGE_WIDTH / 2, currentY, { align: 'center' });
  currentY += 20;

  // Card-style layout - 4 columns
  const cardWidth = 60;
  const cardSpacing = 8;
  // Calculate total width and center it on the page
  const totalCardsWidth = (cardWidth * 4) + (cardSpacing * 3); // 4 cards + 3 gaps
  const cardStartX = (PAGE_WIDTH - totalCardsWidth) / 2;
  const col1X = cardStartX;
  const col2X = cardStartX + cardWidth + cardSpacing;
  const col3X = cardStartX + (cardWidth + cardSpacing) * 2;
  const col4X = cardStartX + (cardWidth + cardSpacing) * 3;
  const cardTop = currentY;
  const cardHeight = 50;

  // Draw card borders
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.5);
  pdf.rect(col1X, cardTop, cardWidth, cardHeight);
  pdf.rect(col2X, cardTop, cardWidth, cardHeight);
  pdf.rect(col3X, cardTop, cardWidth, cardHeight);
  pdf.rect(col4X, cardTop, cardWidth, cardHeight);

  // Card 1: Address
  let cardY = cardTop + 8;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Address', col1X + cardWidth / 2, cardY, { align: 'center' });
  cardY += 8;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('2000 Pisgah Church Road', col1X + cardWidth / 2, cardY, { align: 'center' });
  cardY += 5;
  pdf.text('Greensboro, NC 27455', col1X + cardWidth / 2, cardY, { align: 'center' });

  // Card 2: Phone
  cardY = cardTop + 8;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Phone', col2X + cardWidth / 2, cardY, { align: 'center' });
  cardY += 10;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 100, 180);
  pdf.textWithLink('(336) 510-7840', col2X + cardWidth / 2, cardY, { url: 'tel:+13365107840', align: 'center' });
  pdf.setTextColor(0, 0, 0);

  // Card 3: Email
  cardY = cardTop + 8;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Email', col3X + cardWidth / 2, cardY, { align: 'center' });
  cardY += 8;
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 100, 180);
  pdf.textWithLink('sales@guerrilla-rf.com', col3X + cardWidth / 2, cardY, { url: 'mailto:sales@guerrilla-rf.com', align: 'center' });
  cardY += 5;
  pdf.textWithLink('applications@guerrilla-rf.com', col3X + cardWidth / 2, cardY, { url: 'mailto:applications@guerrilla-rf.com', align: 'center' });
  cardY += 5;
  pdf.textWithLink('careers@guerrilla-rf.com', col3X + cardWidth / 2, cardY, { url: 'mailto:careers@guerrilla-rf.com', align: 'center' });
  cardY += 5;
  pdf.textWithLink('rohs@guerrilla-rf.com', col3X + cardWidth / 2, cardY, { url: 'mailto:rohs@guerrilla-rf.com', align: 'center' });
  pdf.setTextColor(0, 0, 0);

  // Card 4: Social Media
  cardY = cardTop + 8;
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Social Media', col4X + cardWidth / 2, cardY, { align: 'center' });
  cardY += 10;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 100, 180);
  pdf.textWithLink('LinkedIn', col4X + cardWidth / 2, cardY, { url: 'https://www.linkedin.com/company/guerrilla-rf', align: 'center' });
  cardY += 6;
  pdf.textWithLink('Twitter / X', col4X + cardWidth / 2, cardY, { url: 'https://x.com/GuerrillaRF', align: 'center' });
  pdf.setTextColor(0, 0, 0);

  currentY = cardTop + cardHeight + 15;

  // Website link centered below cards
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(0, 100, 180);
  pdf.textWithLink('www.guerrilla-rf.com', PAGE_WIDTH / 2, currentY, { url: 'https://www.guerrilla-rf.com', align: 'center' });
  pdf.setTextColor(0, 0, 0);

  addPageNumber();

  // Return PDF and canvas placeholder info
  return {
    pdf,
    canvasPlaceholder: canvasPlaceholderTitle,
    currentPage
  };
};

/**
 * Calculate bounding box of all nodes on the canvas
 */
const calculateNodesBoundingBox = (nodes) => {
  if (!nodes || nodes.length === 0) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  nodes.forEach(node => {
    if (node.position) {
      // Typical node dimensions (approximate)
      const nodeWidth = 220;
      const nodeHeight = 180;

      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + nodeWidth);
      maxY = Math.max(maxY, node.position.y + nodeHeight);
    }
  });

  if (minX === Infinity) {
    return null;
  }

  // Add padding around the bounding box (reduced padding)
  const padding = 20;

  return {
    x: minX - padding,
    y: minY - padding,
    width: maxX - minX + (padding * 2),
    height: maxY - minY + (padding * 2)
  };
};

/**
 * Capture the React Flow canvas as an image, cropped to content
 * @param {HTMLElement} canvasElement - The React Flow viewport element
 * @param {Array} nodes - Array of nodes to calculate bounding box
 */
export const captureCanvas = async (canvasElement, nodes) => {
  if (!canvasElement) {
    console.warn('Canvas element not found');
    return null;
  }

  try {
    // Get the React Flow container (includes edges, nodes, and pane)
    const reactFlowContainer = document.querySelector('.react-flow');
    const paneElement = document.querySelector('.react-flow__pane');
    const edgesLayer = document.querySelector('.react-flow__edges');
    const captureTarget = reactFlowContainer || canvasElement;

    // Store original styles
    const originalContainerBg = captureTarget.style.backgroundColor;
    const originalPaneBg = paneElement ? paneElement.style.backgroundColor : null;

    // Temporarily change backgrounds to white
    captureTarget.style.backgroundColor = '#ffffff';
    if (paneElement) {
      paneElement.style.backgroundColor = '#ffffff';
    }

    // Log for debugging
    console.log('Capturing canvas with:', {
      container: !!reactFlowContainer,
      pane: !!paneElement,
      edges: !!edgesLayer,
      edgeCount: edgesLayer ? edgesLayer.children.length : 0
    });

    // Use html2canvas which properly renders SVG edges
    const fullCanvas = await html2canvas(captureTarget, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      // Force SVG rendering
      foreignObjectRendering: true,
      removeContainer: false
    });

    // Restore original backgrounds
    captureTarget.style.backgroundColor = originalContainerBg;
    if (paneElement) {
      paneElement.style.backgroundColor = originalPaneBg;
    }

    // If no nodes, return full canvas
    if (!nodes || nodes.length === 0) {
      return fullCanvas.toDataURL('image/png');
    }

    // Calculate bounding box of nodes
    const bbox = calculateNodesBoundingBox(nodes);

    if (!bbox) {
      return fullCanvas.toDataURL('image/png');
    }

    // Get the viewport element
    const viewportElement = document.querySelector('.react-flow__viewport');
    if (!viewportElement) {
      console.warn('Viewport element not found, returning full canvas');
      return fullCanvas.toDataURL('image/png');
    }

    // Extract transform from the viewport
    const transform = viewportElement.style.transform || '';
    const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)\s*scale\(([-\d.]+)\)/);

    let translateX = 0;
    let translateY = 0;
    let scale = 1;

    if (match) {
      translateX = parseFloat(match[1]) || 0;
      translateY = parseFloat(match[2]) || 0;
      scale = parseFloat(match[3]) || 1;
    } else {
      console.warn('Could not parse viewport transform, using defaults');
    }

    console.log('Transform values:', { translateX, translateY, scale });
    console.log('Bounding box:', bbox);
    console.log('Full canvas size:', { width: fullCanvas.width, height: fullCanvas.height });

    // Get the React Flow container offset and dimensions
    const containerRect = captureTarget.getBoundingClientRect();
    console.log('Container dimensions:', {
      width: containerRect.width,
      height: containerRect.height
    });

    // Convert bounding box to screen coordinates
    // html2canvas captures at scale 2, so we need to multiply by 2
    const canvasScale = 2;

    // Transform from world coordinates to screen coordinates
    // Formula: screenCoord = (worldCoord * viewportScale) + viewportTranslate
    const screenBBoxX = (bbox.x * scale) + translateX;
    const screenBBoxY = (bbox.y * scale) + translateY;
    const screenBBoxWidth = bbox.width * scale;
    const screenBBoxHeight = bbox.height * scale;

    console.log('Screen space bounding box:', {
      x: screenBBoxX,
      y: screenBBoxY,
      width: screenBBoxWidth,
      height: screenBBoxHeight
    });

    // Now convert to canvas pixel coordinates (multiply by html2canvas scale)
    let cropX = screenBBoxX * canvasScale;
    let cropY = screenBBoxY * canvasScale;
    let cropWidth = screenBBoxWidth * canvasScale;
    let cropHeight = screenBBoxHeight * canvasScale;

    // Clamp to canvas bounds to handle edge cases
    cropX = Math.max(0, Math.min(cropX, fullCanvas.width - 1));
    cropY = Math.max(0, Math.min(cropY, fullCanvas.height - 1));
    cropWidth = Math.min(fullCanvas.width - cropX, cropWidth);
    cropHeight = Math.min(fullCanvas.height - cropY, cropHeight);

    console.log('Final crop coordinates:', { cropX, cropY, cropWidth, cropHeight });

    // Validate dimensions
    if (cropWidth <= 0 || cropHeight <= 0) {
      console.warn('Invalid crop dimensions, returning full canvas');
      return fullCanvas.toDataURL('image/png');
    }

    // Create a cropped canvas with white background
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropWidth;
    croppedCanvas.height = cropHeight;
    const ctx = croppedCanvas.getContext('2d');

    // Fill with white background first
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cropWidth, cropHeight);

    // Draw the cropped region from the full canvas
    ctx.drawImage(
      fullCanvas,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    return croppedCanvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to capture canvas:', error);
    console.error('Error details:', error.message, error.stack);
    return null;
  }
};

/**
 * Simple canvas capture - captures each node individually and composites them
 * @param {HTMLElement} canvasElement - The React Flow canvas element
 * @param {Object} reactFlowInstance - React Flow instance with getViewport/setViewport methods
 * @param {Array} nodes - Array of node objects with positions
 */
export const captureSignalChainSimple = async (canvasElement, reactFlowInstance, nodes) => {
  debugLog('CAPTURE_START', {
    hasCanvasElement: !!canvasElement,
    nodeCount: nodes?.length,
    nodeIds: nodes?.map(n => n.id)
  });

  if (!canvasElement) {
    debugLog('CAPTURE_FAIL', { reason: 'Canvas element not provided' });
    return null;
  }

  try {
    // Try using toPng on the entire React Flow container
    const reactFlowContainer = document.querySelector('.react-flow');
    if (reactFlowContainer) {
      console.log('Attempting toPng on .react-flow container');

      // Temporarily boost SVG z-index to ensure edges are captured
      // Handle ALL SVGs in the edges container (there may be one per edge)
      const allEdgeSvgs = document.querySelectorAll('.react-flow__edges svg');
      const originalStyles = [];

      allEdgeSvgs.forEach((svg, index) => {
        originalStyles.push(svg.style.zIndex);
        svg.style.zIndex = '1000';

        // Ensure all paths have explicit stroke colors
        const paths = svg.querySelectorAll('path');
        paths.forEach(path => {
          const computedStyle = window.getComputedStyle(path);
          if (!path.getAttribute('stroke') || path.getAttribute('stroke') === 'none') {
            path.setAttribute('stroke', computedStyle.stroke || '#b1b1b7');
          }
          path.setAttribute('stroke-width', computedStyle.strokeWidth || '2');
        });
      });

      console.log('Total edge SVGs found:', allEdgeSvgs.length);

      // Fix watermark SVG colors - inline the computed fill/stroke colors
      // Only process SVGs that are NOT edge SVGs (those are already handled above)
      const watermarkSvgs = reactFlowContainer.querySelectorAll('svg:not(.react-flow__edges svg)');
      const watermarkOriginalStyles = [];

      watermarkSvgs.forEach((svg) => {
        // Skip if this SVG is inside the edges container
        if (svg.closest('.react-flow__edges')) {
          return;
        }

        const paths = svg.querySelectorAll('path');
        const pathStyles = [];

        paths.forEach(path => {
          const computedStyle = window.getComputedStyle(path);
          const originalFill = path.getAttribute('fill');
          const originalStroke = path.getAttribute('stroke');
          const originalOpacity = path.getAttribute('opacity');
          const originalFillOpacity = path.getAttribute('fill-opacity');

          pathStyles.push({ originalFill, originalStroke, originalOpacity, originalFillOpacity });

          // Get computed colors and opacity
          const fill = computedStyle.fill;
          const stroke = computedStyle.stroke;
          const opacity = computedStyle.opacity;

          // Set inline attributes for proper capture
          if (fill && fill !== 'none') {
            path.setAttribute('fill', fill);
            path.setAttribute('fill-opacity', opacity);
          }
          if (stroke && stroke !== 'none') {
            path.setAttribute('stroke', stroke);
            path.setAttribute('stroke-opacity', opacity);
          }
        });

        watermarkOriginalStyles.push(pathStyles);
      });

      console.log('Processed watermark SVGs:', watermarkSvgs.length);

      const dataUrl = await toPng(reactFlowContainer, {
        backgroundColor: '#1a1a2e',
        quality: 1.0,
        pixelRatio: 2
      });

      // Restore original z-index values
      allEdgeSvgs.forEach((svg, index) => {
        if (originalStyles[index] !== undefined) {
          svg.style.zIndex = originalStyles[index];
        }
      });

      // DEBUG: Open captured image in new tab to verify
      // const debugWindow = window.open();
      // debugWindow.document.write(`<img src="${dataUrl}" style="border: 1px solid red; max-width: 100%;" />`);
      // debugWindow.document.title = 'PDF Canvas Capture Debug';

      return dataUrl;
    }

    // Fallback to node-by-node capture
    // Find all node elements in the DOM
    const nodeElements = [];
    const edgesElement = document.querySelector('.react-flow__edges');

    if (!nodes || nodes.length === 0) {
      debugLog('CAPTURE_FAIL', { reason: 'No nodes provided' });
      return null;
    }

    // Calculate the bounding box of all nodes
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    nodes.forEach(node => {
      const nodeEl = document.querySelector(`[data-id="${node.id}"]`);
      if (nodeEl && node.position) {
        const width = nodeEl.offsetWidth || 220;
        const height = nodeEl.offsetHeight || 180;

        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + width);
        maxY = Math.max(maxY, node.position.y + height);

        nodeElements.push({
          element: nodeEl,
          x: node.position.x,
          y: node.position.y,
          width,
          height
        });
      }
    });

    debugLog('CAPTURE_DOM_SEARCH', {
      nodesSearched: nodes.length,
      nodeElementsFound: nodeElements.length,
      hasEdgesElement: !!edgesElement
    });

    if (nodeElements.length === 0) {
      debugLog('CAPTURE_FAIL', { reason: 'No node elements found in DOM' });
      return null;
    }

    // Add padding
    const padding = 40;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const totalWidth = maxX - minX;
    const totalHeight = maxY - minY;

    console.log('Bounding box:', { minX, minY, maxX, maxY, totalWidth, totalHeight });

    // Create a canvas for the composite image
    const scale = 2; // High quality
    const canvas = document.createElement('canvas');
    canvas.width = totalWidth * scale;
    canvas.height = totalHeight * scale;
    const ctx = canvas.getContext('2d');

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // First, capture edges if they exist
    if (edgesElement) {
      try {
        const edgesCanvas = await html2canvas(edgesElement, {
          backgroundColor: null,
          scale: scale,
          logging: false
        });

        // Get viewport transform to position edges correctly
        const viewportElement = document.querySelector('.react-flow__viewport');
        let translateX = 0, translateY = 0, viewportScale = 1;

        if (viewportElement) {
          const transform = viewportElement.style.transform || '';
          const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)\s*scale\(([-\d.]+)\)/);
          if (match) {
            translateX = parseFloat(match[1]) || 0;
            translateY = parseFloat(match[2]) || 0;
            viewportScale = parseFloat(match[3]) || 1;
          }
        }

        // Draw edges on the canvas
        ctx.drawImage(edgesCanvas,
          (-minX) * scale,
          (-minY) * scale
        );
      } catch (e) {
        console.warn('Failed to capture edges:', e);
      }
    }

    // Capture each node individually and place it on the canvas
    for (const nodeInfo of nodeElements) {
      try {
        console.log('Attempting to capture node element:', {
          tagName: nodeInfo.element.tagName,
          className: nodeInfo.element.className,
          offsetWidth: nodeInfo.element.offsetWidth,
          offsetHeight: nodeInfo.element.offsetHeight,
          innerHTML: nodeInfo.element.innerHTML.substring(0, 100)
        });

        const nodeCanvas = await html2canvas(nodeInfo.element, {
          backgroundColor: null,
          scale: scale,
          logging: true,  // Enable html2canvas logging
          useCORS: true,
          allowTaint: true
        });

        console.log('Node canvas result:', {
          width: nodeCanvas.width,
          height: nodeCanvas.height,
          hasContent: nodeCanvas.width > 0 && nodeCanvas.height > 0
        });

        // Calculate position relative to the bounding box
        const x = (nodeInfo.x - minX) * scale;
        const y = (nodeInfo.y - minY) * scale;

        // Draw the node on the composite canvas
        // Use the node's original dimensions * scale to draw correctly
        const drawWidth = nodeInfo.width * scale;
        const drawHeight = nodeInfo.height * scale;
        ctx.drawImage(nodeCanvas, x, y, drawWidth, drawHeight);

        console.log('Captured node at', nodeInfo.x, nodeInfo.y);
      } catch (e) {
        console.warn('Failed to capture node:', e);
      }
    }

    console.log('Canvas captured successfully:', {
      width: canvas.width,
      height: canvas.height,
      nodesCaptured: nodeElements.length
    });

    const dataUrl = canvas.toDataURL('image/png');

    // DEBUG: Open captured image in new tab to verify
    // const debugWindow = window.open();
    // debugWindow.document.write(`<img src="${dataUrl}" style="border: 1px solid red; max-width: 100%;" />`);
    // debugWindow.document.title = 'PDF Canvas Capture Debug';

    return dataUrl;

  } catch (error) {
    console.error('Failed to capture signal chain:', error);
    return null;
  }
};

/**
 * Complete PDF generation with canvas image
 * @param {Object} projectData - All project data
 * @param {HTMLElement} canvasElement - The React Flow canvas element
 * @param {Object} reactFlowInstance - React Flow instance with viewport methods (optional)
 */
export const generateCompletePDFReport = async (projectData, canvasElement, reactFlowInstance) => {
  try {
    debugLog('PDF_REPORT_START', {
      hasCanvasElement: !!canvasElement,
      hasReactFlowInstance: !!reactFlowInstance,
      nodeCount: projectData.nodes?.length
    });

    // Generate base PDF
    const { pdf, canvasPlaceholder, currentPage } = await generateProjectReport(projectData);

    // Capture canvas image using the new simple method (fits view, captures, restores)
    const canvasImage = await captureSignalChainSimple(canvasElement, reactFlowInstance, projectData.nodes);

    debugLog('PDF_CANVAS_CAPTURE_RESULT', {
      hasCanvasImage: !!canvasImage,
      imageLength: canvasImage?.length
    });

    // Go to title page to add canvas image
    pdf.setPage(canvasPlaceholder.page);

    if (canvasImage) {
      // Create temporary image to get dimensions
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = canvasImage;
      });

      // Calculate aspect ratio and dimensions
      const aspectRatio = img.width / img.height;
      let imgWidth = canvasPlaceholder.width;
      let imgHeight = imgWidth / aspectRatio;

      // If image is too tall, scale it down
      // Available height: ~155mm (page 210mm - title ~43mm - footer ~12mm)
      const maxHeight = 150;
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = imgHeight * aspectRatio;
      }

      // Center horizontally if needed
      const xOffset = (canvasPlaceholder.width - imgWidth) / 2;

      debugLog('PDF_ADD_IMAGE', {
        page: canvasPlaceholder.page,
        x: canvasPlaceholder.x + xOffset,
        y: canvasPlaceholder.y,
        imgWidth,
        imgHeight,
        imgNaturalWidth: img.width,
        imgNaturalHeight: img.height,
        aspectRatio
      });

      // We're already on the correct page, just add the image
      pdf.addImage(
        canvasImage,
        'PNG',
        canvasPlaceholder.x + xOffset,
        canvasPlaceholder.y,
        imgWidth,
        imgHeight,
        undefined,
        'FAST'
      );
    }

    // Save PDF
    const fileName = `${projectData.projectName || 'GChain_Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Failed to generate PDF report:', error);
    return { success: false, error: error.message };
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate overall system performance from calculation results
 */
const calculateSystemPerformance = (nodes, calculationResults) => {
  if (!nodes || !calculationResults) {
    return { hasData: false };
  }

  // Filter out source and measurement nodes
  const excludedTypes = ['inputSource', 'noiseSource', 'measurement'];

  let totalGain = 0;
  let outputPower = null;
  let compressionWarnings = 0;
  let compressionErrors = 0;
  const componentDetails = [];

  nodes.forEach(node => {
    // Skip source and measurement nodes
    if (excludedTypes.includes(node.type)) {
      return;
    }

    const results = calculationResults[node.id];
    if (results) {
      const gain = parseFloat(results.gain) || 0;
      const pout = results.outputPower ? parseFloat(results.outputPower) : null;
      const cascadedGain = results.cumulativeGain ? parseFloat(results.cumulativeGain) : null;

      let status = 'OK';
      if (results.compressionState === 'compressed') {
        status = 'Compressed';
        compressionErrors++;
      } else if (results.compressionState === 'warning') {
        status = 'Warning';
        compressionWarnings++;
      }

      componentDetails.push({
        name: (node.data?.alias || node.data?.name || node.type || 'Unknown').substring(0, 30),
        gain: gain !== 0 ? gain.toFixed(2) : '-',
        outputPower: pout !== null ? pout.toFixed(2) : '-',
        cascadedGain: cascadedGain !== null ? cascadedGain.toFixed(2) : '-',
        status
      });

      if (pout !== null) {
        outputPower = pout;
      }

      if (cascadedGain !== null) {
        totalGain = cascadedGain;
      }
    }
  });

  return {
    hasData: componentDetails.length > 0,
    totalGain: totalGain.toFixed(2),
    outputPower: outputPower !== null ? outputPower.toFixed(2) : null,
    compressionWarnings,
    compressionErrors,
    componentDetails
  };
};

/**
 * Count components by category
 */
const countComponentsByCategory = (nodes) => {
  const counts = {};

  nodes.forEach(node => {
    const type = node.type || 'Unknown';
    const category = getCategoryName(type);
    counts[category] = (counts[category] || 0) + 1;
  });

  return counts;
};

/**
 * Get human-readable category name from component type
 */
const getCategoryName = (type) => {
  const categoryMap = {
    'inputSource': 'Signal Generators',
    'noiseSource': 'Noise Sources',
    'lna': 'Amplifiers',
    'powerAmplifier': 'Amplifiers',
    'vga': 'Amplifiers',
    'attenuator': 'Attenuators',
    'variableAttenuator': 'Attenuators',
    'cable': 'Cables',
    'splitter': 'Splitters',
    'combiner': 'Combiners',
    'bandpassFilter': 'Filters',
    'lowpassFilter': 'Filters',
    'highpassFilter': 'Filters',
    'bandstopFilter': 'Filters',
    'passiveMixer': 'Mixers',
    'activeMixer': 'Mixers',
    'spdtSwitch': 'Switches',
    'sp4tSwitch': 'Switches',
    'termination': 'Terminations',
    'note': 'Notes',
    'generic': 'Generic Components'
  };

  return categoryMap[type] || 'Other Components';
};

/**
 * Extract key parameters from a node
 */
const getKeyParameters = (node) => {
  if (!node.data || !node.data.parameters) {
    return 'N/A';
  }

  const params = node.data.parameters;
  const keyParams = [];

  // Common important parameters
  if (params.frequency) {
    keyParams.push(`F: ${params.frequency.value}${params.frequency.unit}`);
  }
  if (params.power) {
    keyParams.push(`P: ${params.power.value}${params.power.unit}`);
  }
  if (params.gain) {
    keyParams.push(`G: ${params.gain.value}${params.gain.unit}`);
  }
  if (params.nf) {
    keyParams.push(`NF: ${params.nf.value}${params.nf.unit}`);
  }
  if (params.p1db) {
    keyParams.push(`OP1dB: ${params.p1db.value}${params.p1db.unit}`);
  }

  return keyParams.length > 0 ? keyParams.join(', ') : 'N/A';
};

export default {
  generateCompletePDFReport,
  generateProjectReport,
  captureCanvas,
  captureSignalChainSimple
};
